import React, { useState } from "react";
import { ChevronDown, Globe, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";

// Destinos canónicos (Ventas, oct-2026): evita «Italy / Italia» o «Spain / España» mezclados.
export const DESTINATIONS = ["España", "Italia", "Portugal", "Malta", "Marruecos"];
const ALIASES = {
  "españa": "España", "espana": "España", "spain": "España", "spagna": "España", "espagne": "España",
  "italia": "Italia", "italy": "Italia", "italie": "Italia",
  "portugal": "Portugal",
  "malta": "Malta",
  "marruecos": "Marruecos", "morocco": "Marruecos", "maroc": "Marruecos", "marocco": "Marruecos",
};
export const SEP = " · ";

// «Spain · Italy · España» → ["España", "Italia"]; lo no reconocido se conserva tal cual.
export const parseDestinations = (text) => {
  const out = [];
  (text || "").split(/\s*(?:·|,|\/|\+|&)\s*/).map((p) => p.trim()).filter(Boolean).forEach((p) => {
    const canon = ALIASES[p.toLowerCase()] || p;
    if (!out.includes(canon)) out.push(canon);
  });
  return out;
};
const sortCanon = (list) => [...list].sort((a, b) => {
  const ia = DESTINATIONS.indexOf(a), ib = DESTINATIONS.indexOf(b);
  return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
});

export const displayDestination = (text) => parseDestinations(text).join(SEP);

export function DestinationMultiSelect({ value, onChange, testid = "destination-select" }) {
  const [open, setOpen] = useState(false);
  const selected = parseDestinations(value);
  const toggle = (d) => {
    const next = selected.includes(d) ? selected.filter((x) => x !== d) : [...selected, d];
    onChange(sortCanon(next).join(SEP));
  };
  const others = selected.filter((d) => !DESTINATIONS.includes(d));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="flex min-h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-left text-sm shadow-sm hover:bg-accent/40 focus:outline-none focus:ring-1 focus:ring-ring" data-testid={testid}>
          <span className="flex min-w-0 flex-wrap items-center gap-1">
            {selected.length === 0 && <span className="flex items-center gap-1.5 text-muted-foreground"><Globe className="h-3.5 w-3.5" /> Elegir destino(s)…</span>}
            {selected.map((d) => (
              <span key={d} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${DESTINATIONS.includes(d) ? "bg-primary/10 text-primary" : "bg-amber-100 text-amber-800"}`} data-testid={`${testid}-chip-${d}`}>
                {d}
                <span role="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); toggle(d); }} className="rounded-full hover:bg-black/10" aria-label={`Quitar ${d}`} data-testid={`${testid}-remove-${d}`}><X className="h-3 w-3" /></span>
              </span>
            ))}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2" data-testid={`${testid}-options`}>
        <p className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Puedes marcar varios (viajes combinados)</p>
        {DESTINATIONS.map((d) => (
          <label key={d} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent" data-testid={`${testid}-opt-${d}`}>
            <Checkbox checked={selected.includes(d)} onCheckedChange={() => toggle(d)} />
            {d}
          </label>
        ))}
        {others.length > 0 && (
          <p className="mt-2 border-t px-1 pt-2 text-[10px] text-amber-700">Valor antiguo no estándar: {others.join(", ")}. Quítalo con la × y elige el destino de la lista.</p>
        )}
      </PopoverContent>
    </Popover>
  );
}
